/**
 * LotSmith — Magic-link login.
 *
 * Form submits to worker-auth POST /v1/auth/magic-link. In M3 we stub the
 * request because worker-auth lands in M4 — we simulate the success/error
 * UX so the component is real by the time it gets wired.
 */
import { useState } from "react";
import { Link } from "react-router-dom";

type State = "idle" | "submitting" | "sent" | "error";

export function Login() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError("Enter a valid email.");
      return;
    }
    setState("submitting");
    try {
      const res = await fetch(`${apiBase()}/v1/auth/magic-link`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok && res.status !== 202) {
        throw new Error(`Request failed (${res.status})`);
      }
      setState("sent");
    } catch (err) {
      // M3: worker-auth does not exist yet — treat network failure as success
      // for the UX flow so we can demo the page. M4 removes this fallback.
      if (import.meta.env.DEV) {
        console.warn("[login] API not available — dev stub success", err);
        setState("sent");
        return;
      }
      setState("error");
      setError(err instanceof Error ? err.message : "Unknown error");
    }
  }

  return (
    <div className="auth">
      <Link to="/" className="auth__brand">LotSmith</Link>
      <div className="auth__card">
        {state === "sent" ? (
          <div className="auth__sent">
            <h1>Check your email</h1>
            <p>
              We sent a sign-in link to <strong>{email}</strong>. It expires in
              15 minutes.
            </p>
            <button
              type="button"
              className="btn btn--ghost"
              onClick={() => setState("idle")}
            >
              Use a different email
            </button>
          </div>
        ) : (
          <>
            <h1>Sign in</h1>
            <p className="auth__lead">
              We&rsquo;ll email you a one-time sign-in link. No password.
            </p>
            <form onSubmit={onSubmit} noValidate>
              <label htmlFor="email" className="label">Email</label>
              <input
                id="email"
                type="email"
                inputMode="email"
                autoComplete="email"
                required
                className="input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={state === "submitting"}
                placeholder="you@example.com"
              />
              {error && <div className="form-error">{error}</div>}
              <button
                type="submit"
                className="btn btn--primary btn--block"
                disabled={state === "submitting"}
              >
                {state === "submitting" ? "Sending…" : "Send sign-in link"}
              </button>
            </form>
          </>
        )}
      </div>
      <p className="auth__foot">
        New to LotSmith? Same form — we&rsquo;ll create your account.
      </p>
    </div>
  );
}

function apiBase(): string {
  return (import.meta.env.VITE_AUTH_API_BASE as string | undefined) ?? "";
}
